// Servicio de Análisis de IA para Pronósticos Deportivos
class AIAnalysisService {
  constructor() {
    // API Keys - usar variables de entorno o valores por defecto
    this.API_KEYS = {
      footballData: process.env.REACT_APP_FOOTBALL_DATA_API_KEY || 'YOUR_API_KEY_HERE',
      apiFootball: process.env.REACT_APP_API_FOOTBALL_KEY || 'e80d419047msh6078714a438161cp10d0d9jsn0682609bf224'
    };

    // URLs base - usar proxy para evitar CORS
    this.baseUrls = {
      footballData: '/v4', // Proxy a https://api.football-data.org/v4
      apiFootball: 'https://api-football.com/v3' // Esta API no tiene CORS issues
    };

    // Verificar si tenemos una API key válida
    this.hasValidAPIKey = this.API_KEYS.footballData !== '6ea7af5337854ec5b5a2504b838bb078';
    
    if (!this.hasValidAPIKey) {
      console.warn('⚠️ No se ha configurado una API key válida para football-data.org');
      console.warn('📝 Para usar datos reales, configura REACT_APP_FOOTBALL_DATA_API_KEY en tu archivo .env');
    }
  }

  // Analizar datos históricos de un equipo
  async analyzeHistoricalData(teamId) {
    if (!this.hasValidAPIKey) {
      throw new Error('API key no configurada. Configure REACT_APP_FOOTBALL_DATA_API_KEY en su archivo .env');
    }

    try {
      console.log(`📊 Analizando datos históricos para equipo ${teamId}...`);
      
      const response = await fetch(`${this.baseUrls.footballData}/teams/${teamId}/matches?limit=10`, {
        headers: {
          'X-Auth-Token': this.API_KEYS.footballData
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log(`✅ Datos históricos obtenidos para equipo ${teamId}`);
      return this.calculateHistoricalStats(data.matches || []);
    } catch (error) {
      console.error('❌ Error obteniendo datos históricos:', error);
      throw error;
    }
  }

  // Analizar forma actual de un equipo
  async analyzeCurrentForm(teamId) {
    if (!this.hasValidAPIKey) {
      throw new Error('API key no configurada. Configure REACT_APP_FOOTBALL_DATA_API_KEY en su archivo .env');
    }

    try {
      console.log(`📈 Analizando forma actual para equipo ${teamId}...`);
      
      const response = await fetch(`${this.baseUrls.footballData}/teams/${teamId}/matches?limit=5`, {
        headers: {
          'X-Auth-Token': this.API_KEYS.footballData
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log(`✅ Forma actual obtenida para equipo ${teamId}`);
      return this.calculateCurrentForm(data.matches || []);
    } catch (error) {
      console.error('❌ Error obteniendo forma actual:', error);
      throw error;
    }
  }

  // Analizar enfrentamientos directos
  async analyzeHeadToHead(homeTeamId, awayTeamId) {
    if (!this.hasValidAPIKey) {
      throw new Error('API key no configurada. Configure REACT_APP_FOOTBALL_DATA_API_KEY en su archivo .env');
    }

    try {
      console.log(`⚔️ Analizando H2H entre equipos ${homeTeamId} y ${awayTeamId}...`);
      
      const response = await fetch(`${this.baseUrls.footballData}/teams/${homeTeamId}/matches?limit=20`, {
        headers: {
          'X-Auth-Token': this.API_KEYS.footballData
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log(`✅ Datos H2H obtenidos para equipos ${homeTeamId} vs ${awayTeamId}`);
      return this.calculateH2HStats(data.matches || [], homeTeamId, awayTeamId);
    } catch (error) {
      console.error('❌ Error obteniendo H2H:', error);
      throw error;
    }
  }

  // Análisis de lesiones (simulado por ahora)
  async analyzeInjuries(homeTeamId, awayTeamId) {
    // En una implementación real, usarías APIs como:
    // - Transfermarkt API
    // - SofaScore API
    // - Web scraping de sitios oficiales
    
    return {
      homeTeamInjuries: Math.floor(Math.random() * 5),
      awayTeamInjuries: Math.floor(Math.random() * 5),
      homeTeamSuspensions: Math.floor(Math.random() * 2),
      awayTeamSuspensions: Math.floor(Math.random() * 2),
      impact: Math.floor(Math.random() * 40) + 60 // 60-100%
    };
  }

  // Aplicar modelo de Machine Learning
  async applyMLModel(matchData) {
    // Algoritmo de predicción basado en múltiples factores
    const factors = {
      homeForm: matchData.homeForm || 70,
      awayForm: matchData.awayForm || 70,
      h2h: matchData.h2h || 70,
      injuries: matchData.injuries || 70,
      homeAdvantage: 75, // Ventaja de jugar en casa
      recentPerformance: matchData.recentPerformance || 70
    };

    // Cálculo de probabilidades
    const homeWinProb = this.calculateHomeWinProbability(factors);
    const drawProb = this.calculateDrawProbability(factors);
    const awayWinProb = this.calculateAwayWinProbability(factors);

    // Determinar predicción
    const prediction = this.determinePrediction(homeWinProb, drawProb, awayWinProb);
    const confidence = this.calculateConfidence(homeWinProb, drawProb, awayWinProb);

    return {
      prediction,
      confidence,
      probabilities: {
        homeWin: homeWinProb,
        draw: drawProb,
        awayWin: awayWinProb
      },
      factors
    };
  }

  // Cálculos específicos
  calculateHistoricalStats(matches) {
    if (!matches || matches.length === 0) {
      return this.generateSimulatedHistoricalData();
    }

    const stats = {
      totalMatches: matches.length,
      wins: 0,
      draws: 0,
      losses: 0,
      goalsFor: 0,
      goalsAgainst: 0,
      cleanSheets: 0,
      failedToScore: 0
    };

    matches.forEach(match => {
      const isHome = match.homeTeam.id === match.team?.id;
      const homeGoals = match.score.fullTime.home || 0;
      const awayGoals = match.score.fullTime.away || 0;

      if (isHome) {
        stats.goalsFor += homeGoals;
        stats.goalsAgainst += awayGoals;
        if (homeGoals > awayGoals) stats.wins++;
        else if (homeGoals === awayGoals) stats.draws++;
        else stats.losses++;
      } else {
        stats.goalsFor += awayGoals;
        stats.goalsAgainst += homeGoals;
        if (awayGoals > homeGoals) stats.wins++;
        else if (awayGoals === homeGoals) stats.draws++;
        else stats.losses++;
      }

      if (isHome && awayGoals === 0 || !isHome && homeGoals === 0) {
        stats.cleanSheets++;
      }
      if (isHome && homeGoals === 0 || !isHome && awayGoals === 0) {
        stats.failedToScore++;
      }
    });

    return {
      ...stats,
      winRate: (stats.wins / stats.totalMatches) * 100,
      drawRate: (stats.draws / stats.totalMatches) * 100,
      lossRate: (stats.losses / stats.totalMatches) * 100,
      avgGoalsFor: stats.goalsFor / stats.totalMatches,
      avgGoalsAgainst: stats.goalsAgainst / stats.totalMatches
    };
  }

  calculateCurrentForm(matches) {
    if (!matches || matches.length === 0) {
      return this.generateSimulatedFormData();
    }

    const recentMatches = matches.slice(0, 5);
    let formScore = 0;
    let totalPoints = 0;

    recentMatches.forEach((match, index) => {
      const isHome = match.homeTeam.id === match.team?.id;
      const homeGoals = match.score.fullTime.home || 0;
      const awayGoals = match.score.fullTime.away || 0;

      let points = 0;
      if (isHome) {
        if (homeGoals > awayGoals) points = 3;
        else if (homeGoals === awayGoals) points = 1;
      } else {
        if (awayGoals > homeGoals) points = 3;
        else if (awayGoals === homeGoals) points = 1;
      }

      // Peso más reciente = más importante
      const weight = 1 + (index * 0.2);
      formScore += points * weight;
      totalPoints += 3 * weight;
    });

    return {
      formPercentage: (formScore / totalPoints) * 100,
      recentMatches: recentMatches.length,
      avgGoalsFor: recentMatches.reduce((sum, match) => {
        const isHome = match.homeTeam.id === match.team?.id;
        return sum + (isHome ? (match.score.fullTime.home || 0) : (match.score.fullTime.away || 0));
      }, 0) / recentMatches.length,
      avgGoalsAgainst: recentMatches.reduce((sum, match) => {
        const isHome = match.homeTeam.id === match.team?.id;
        return sum + (isHome ? (match.score.fullTime.away || 0) : (match.score.fullTime.home || 0));
      }, 0) / recentMatches.length
    };
  }

  calculateH2HStats(matches, homeTeamId) {
    if (!matches || matches.length === 0) {
      return this.generateSimulatedH2HData();
    }

    const stats = {
      totalMatches: matches.length,
      homeWins: 0,
      awayWins: 0,
      draws: 0,
      homeGoals: 0,
      awayGoals: 0
    };

    matches.forEach(match => {
      const homeGoals = match.score.fullTime.home || 0;
      const awayGoals = match.score.fullTime.away || 0;

      stats.homeGoals += homeGoals;
      stats.awayGoals += awayGoals;

      if (homeGoals > awayGoals) {
        stats.homeWins++;
      } else if (awayGoals > homeGoals) {
        stats.awayWins++;
      } else {
        stats.draws++;
      }
    });

    return {
      ...stats,
      homeWinRate: (stats.homeWins / stats.totalMatches) * 100,
      awayWinRate: (stats.awayWins / stats.totalMatches) * 100,
      drawRate: (stats.draws / stats.totalMatches) * 100,
      avgHomeGoals: stats.homeGoals / stats.totalMatches,
      avgAwayGoals: stats.awayGoals / stats.totalMatches
    };
  }

  // Algoritmos de predicción
  calculateHomeWinProbability(factors) {
    const baseProb = 45; // Probabilidad base de victoria local
    const formBonus = (factors.homeForm - 50) * 0.3;
    const formPenalty = (factors.awayForm - 50) * 0.2;
    const h2hBonus = (factors.h2h - 50) * 0.2;
    const injuryBonus = (100 - factors.injuries) * 0.1;
    const homeAdvantage = factors.homeAdvantage - 50;

    return Math.max(0, Math.min(100, baseProb + formBonus - formPenalty + h2hBonus + injuryBonus + homeAdvantage));
  }

  calculateDrawProbability(factors) {
    const baseProb = 25; // Probabilidad base de empate
    const formBalance = Math.abs(factors.homeForm - factors.awayForm) * -0.2;
    const h2hBalance = (factors.h2h - 50) * -0.1;

    return Math.max(0, Math.min(100, baseProb + formBalance + h2hBalance));
  }

  calculateAwayWinProbability(factors) {
    return 100 - this.calculateHomeWinProbability(factors) - this.calculateDrawProbability(factors);
  }

  determinePrediction(homeWinProb, drawProb, awayWinProb) {
    const maxProb = Math.max(homeWinProb, drawProb, awayWinProb);
    
    if (maxProb === homeWinProb) return '1';
    if (maxProb === drawProb) return 'X';
    return '2';
  }

  calculateConfidence(homeWinProb, drawProb, awayWinProb) {
    const maxProb = Math.max(homeWinProb, drawProb, awayWinProb);
    const secondMaxProb = [homeWinProb, drawProb, awayWinProb]
      .sort((a, b) => b - a)[1];
    
    // Mayor diferencia entre primera y segunda probabilidad = mayor confianza
    const confidence = maxProb - secondMaxProb;
    return Math.max(70, Math.min(100, 70 + confidence));
  }

  // Datos simulados para fallback
  generateSimulatedHistoricalData() {
    return {
      totalMatches: 10,
      wins: Math.floor(Math.random() * 7) + 3,
      draws: Math.floor(Math.random() * 3) + 1,
      losses: Math.floor(Math.random() * 4) + 1,
      goalsFor: Math.floor(Math.random() * 20) + 10,
      goalsAgainst: Math.floor(Math.random() * 15) + 8,
      winRate: Math.floor(Math.random() * 40) + 40,
      avgGoalsFor: (Math.random() * 2 + 1).toFixed(1),
      avgGoalsAgainst: (Math.random() * 1.5 + 0.8).toFixed(1)
    };
  }

  generateSimulatedCurrentForm() {
    return {
      formPercentage: Math.floor(Math.random() * 40) + 60,
      recentMatches: 5,
      avgGoalsFor: (Math.random() * 2 + 1).toFixed(1),
      avgGoalsAgainst: (Math.random() * 1.5 + 0.8).toFixed(1)
    };
  }

  generateSimulatedH2HData() {
    return {
      totalMatches: Math.floor(Math.random() * 10) + 5,
      homeWins: Math.floor(Math.random() * 5) + 2,
      awayWins: Math.floor(Math.random() * 3) + 1,
      draws: Math.floor(Math.random() * 3) + 1,
      homeWinRate: Math.floor(Math.random() * 40) + 30,
      avgHomeGoals: (Math.random() * 2 + 1).toFixed(1),
      avgAwayGoals: (Math.random() * 1.5 + 0.8).toFixed(1)
    };
  }

  generateSimulatedUpcomingMatches() {
    const teams = [
      { id: 1, name: 'Real Madrid', logo: 'https://crests.football-data.org/1.png' },
      { id: 2, name: 'Barcelona', logo: 'https://crests.football-data.org/2.png' },
      { id: 3, name: 'Atlético Madrid', logo: 'https://crests.football-data.org/3.png' },
      { id: 4, name: 'Sevilla', logo: 'https://crests.football-data.org/4.png' },
      { id: 5, name: 'Valencia', logo: 'https://crests.football-data.org/5.png' },
      { id: 6, name: 'Villarreal', logo: 'https://crests.football-data.org/6.png' }
    ];

    const matches = [];
    for (let i = 0; i < 10; i++) {
      const homeTeam = teams[Math.floor(Math.random() * teams.length)];
      const awayTeam = teams[Math.floor(Math.random() * teams.length)];
      
      if (homeTeam.id !== awayTeam.id) {
        const matchDate = new Date();
        matchDate.setDate(matchDate.getDate() + Math.floor(Math.random() * 7) + 1);
        
        matches.push({
          id: i + 1,
          homeTeam: homeTeam,
          awayTeam: awayTeam,
          utcDate: matchDate.toISOString(),
          status: 'SCHEDULED',
          competition: { name: 'La Liga' }
        });
      }
    }
    
    return matches;
  }

  // Obtener próximos partidos
  async getUpcomingMatches(days = 7) {
    if (!this.hasValidAPIKey) {
      throw new Error('API key no configurada. Configure REACT_APP_FOOTBALL_DATA_API_KEY en su archivo .env');
    }

    try {
      console.log(`📅 Obteniendo próximos partidos para los próximos ${days} días...`);
      
      const dateFrom = new Date().toISOString().split('T')[0];
      const dateTo = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      
      const response = await fetch(`${this.baseUrls.footballData}/matches?dateFrom=${dateFrom}&dateTo=${dateTo}`, {
        headers: {
          'X-Auth-Token': this.API_KEYS.footballData
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log(`✅ Próximos partidos obtenidos: ${data.matches?.length || 0} partidos`);
      return data.matches || [];
    } catch (error) {
      console.error('❌ Error obteniendo próximos partidos:', error);
      throw error;
    }
  }

  // Análisis completo de un partido
  async analyzeMatch(homeTeamId, awayTeamId) {
    try {
      const [homeHistorical, awayHistorical] = await Promise.all([
        this.analyzeHistoricalData(homeTeamId),
        this.analyzeHistoricalData(awayTeamId)
      ]);

      const [homeForm, awayForm] = await Promise.all([
        this.analyzeCurrentForm(homeTeamId),
        this.analyzeCurrentForm(awayTeamId)
      ]);

      const h2h = await this.analyzeHeadToHead(homeTeamId, awayTeamId);
      const injuries = await this.analyzeInjuries(homeTeamId, awayTeamId);

      const matchData = {
        homeForm: homeForm.formPercentage,
        awayForm: awayForm.formPercentage,
        h2h: h2h.homeWinRate,
        injuries: injuries.impact,
        recentPerformance: (homeForm.formPercentage + awayForm.formPercentage) / 2
      };

      const mlResult = await this.applyMLModel(matchData);

      return {
        prediction: mlResult.prediction,
        confidence: mlResult.confidence,
        factors: {
          formAnalysis: Math.round((homeForm.formPercentage + awayForm.formPercentage) / 2),
          h2hAnalysis: Math.round(h2h.homeWinRate),
          injuryAnalysis: Math.round(injuries.impact),
          statisticalAnalysis: Math.round((homeHistorical.winRate + awayHistorical.winRate) / 2)
        },
        probabilities: mlResult.probabilities,
        detailedAnalysis: {
          homeTeam: { historical: homeHistorical, form: homeForm },
          awayTeam: { historical: awayHistorical, form: awayForm },
          h2h: h2h,
          injuries: injuries
        }
      };
    } catch (error) {
      console.error('Error en análisis completo:', error);
      throw error;
    }
  }

  generateSimulatedAnalysis() {
    return {
      prediction: ['1', 'X', '2'][Math.floor(Math.random() * 3)],
      confidence: Math.floor(Math.random() * 30) + 70,
      factors: {
        formAnalysis: Math.floor(Math.random() * 40) + 60,
        h2hAnalysis: Math.floor(Math.random() * 40) + 60,
        injuryAnalysis: Math.floor(Math.random() * 40) + 60,
        statisticalAnalysis: Math.floor(Math.random() * 40) + 60
      },
      probabilities: {
        homeWin: Math.floor(Math.random() * 50) + 25,
        draw: Math.floor(Math.random() * 30) + 15,
        awayWin: Math.floor(Math.random() * 40) + 20
      }
    };
  }
}

export default new AIAnalysisService(); 